/**
 * API Helper для управления сервисами судов
 * Требует авторизацию через JWT токен
 * 
 * Все запросы идут через centralized elBackAxios
 */

import { elBackAxios, myAxios } from '@/services/myAxios'
import { getMwendBaseUrl } from '@/utils/api-config'

/**
 * Загрузить все корабли и их сервисы (для Admin Panel)
 * @returns {Promise<Array>}
 */
export async function getAllDevicesWithServices() {
  try {
    const response = await elBackAxios.get('api/services/admin/all-devices')
    return response.data
  } catch (error) {
    console.error('Failed to load devices:', error)
    throw error
  }
}

/**
 * Получить сервисы конкретного корабля
 * @param {string} deviceId
 * @returns {Promise<Array>}
 */
export async function getDeviceServices(deviceId) {
  try {
    const response = await elBackAxios.get(`/api/services/${deviceId}`)
    return response.data
  } catch (error) {
    console.error(`Failed to load services for device ${deviceId}:`, error)
    throw error
  }
}

/**
 * Подписать корабль на сервис
 * @param {string} deviceId
 * @param {string} serviceType - AIS, Navarea, Weather, Catalog
 * @param {string|null} endDate - ISO date string (null for unlimited)
 * @param {string|null} notes
 * @returns {Promise<Object>}
 */
export async function subscribeToService(deviceId, serviceType, endDate = null, notes = null) {
  try {
    const payload = {
      service_type: serviceType
    }

    if (endDate) {
      payload.end_date = endDate
    }

    if (notes) {
      payload.notes = notes
    }

    const response = await elBackAxios.post(`/api/services/${deviceId}/subscribe`, payload)
    return response.data
  } catch (error) {
    console.error(`Failed to subscribe to ${serviceType}:`, error)
    throw error
  }
}

/**
 * Обновить существующую подписку на сервис
 * @param {string} deviceId
 * @param {string} serviceType - AIS, Navarea, Weather, Catalog
 * @param {string|null} endDate - ISO date string (null for unlimited)
 * @param {string|null} notes
 * @returns {Promise<Object>}
 */
export async function updateService(deviceId, serviceType, endDate = null, notes = null, status = null) {
  try {
    const payload = {}

    if (endDate) {
      payload.end_date = endDate
    } else {
      payload.end_date = null
    }

    if (notes !== undefined) {
      payload.notes = notes
    }

    if (status) {
      payload.status = status
    }

    const response = await elBackAxios.put(`/api/services/${deviceId}/${serviceType}`, payload)
    return response.data
  } catch (error) {
    console.error(`Failed to update ${serviceType}:`, error)
    throw error
  }
}

/**
 * Приостановить сервис
 * @param {string} deviceId
 * @param {string} serviceType
 * @returns {Promise<Object>}
 */
export async function pauseService(deviceId, serviceType) {
  try {
    const response = await elBackAxios.post(`/api/services/${deviceId}/pause/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to pause ${serviceType}:`, error)
    throw error
  }
}

/**
 * Возобновить сервис
 * @param {string} deviceId
 * @param {string} serviceType
 * @returns {Promise<Object>}
 */
export async function resumeService(deviceId, serviceType) {
  try {
    const response = await elBackAxios.post(`/api/services/${deviceId}/resume/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to resume ${serviceType}:`, error)
    throw error
  }
}

/**
 * Отписать корабль от сервиса
 * @param {string} deviceId
 * @param {string} serviceType
 * @returns {Promise<Object>}
 */
export async function unsubscribeFromService(deviceId, serviceType) {
  try {
    const response = await elBackAxios.post(`/api/services/${deviceId}/unsubscribe/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to unsubscribe from ${serviceType}:`, error)
    throw error
  }
}

/**
 * Получить статус сервиса с цветовым кодом
 * @param {string} status
 * @returns {Object}
 */
export function getStatusInfo(status) {
  const statusMap = {
    'Active': {
      color: 'success',
      icon: '🟢',
      label: 'Active',
      badge: 'success'
    },
    'Paused': {
      color: 'danger',
      icon: '🔴',
      label: 'Paused',
      badge: 'danger'
    },
    'Canceled': {
      color: 'secondary',
      icon: '⚪',
      label: 'Canceled',
      badge: 'secondary'
    },
    'Expired': {
      color: 'warning',
      icon: '🟡',
      label: 'Expired',
      badge: 'warning'
    }
  }

  return statusMap[status] || statusMap['Canceled']
}

/**
 * Проверить, является ли дата истекшей
 * @param {string|null} endDate
 * @returns {boolean}
 */
export function isDateExpired(endDate) {
  if (!endDate) {
    return false
  }

  const end = new Date(endDate)
  const now = new Date()
  return end < now
}

/**
 * Форматировать дату для отображения
 * @param {string|null} date
 * @returns {string}
 */
export function formatDate(date) {
  if (!date) {
    return 'Unlimited'
  }

  try {
    const d = new Date(date)
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return date
  }
}

/**
 * Получить статус всех сервисов (health check)
 * @returns {Promise<Object>}
 */
export async function getAllServicesHealth() {
  try {
    const response = await elBackAxios.get('api/services/admin/health')
    return response.data
  } catch (error) {
    console.error('Failed to get services health:', error)
    throw error
  }
}

/**
 * Получить статус конкретного сервиса (health check)
 * @param {string} serviceType - Тип сервиса (AIS, Navarea, Weather, Catalog)
 * @returns {Promise<Object>}
 */
export async function getServiceHealth(serviceType) {
  try {
    const response = await elBackAxios.get(`/api/services/admin/health/${serviceType}`)
    return response.data
  } catch (error) {
    console.error(`Failed to get ${serviceType} health:`, error)
    throw error
  }
}

/**
 * Получить Navarea данные для корабля если он подписан на сервис
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<Object>} - Navarea данные с результатами и сообщениями
 */
/**
 * Загрузить ВСЕ Navarea данные (без проверки подписки)
 * Backend отдает все данные, frontend проверяет подписку для каждого корабля
 * @returns {Promise<Object>}
 */
export async function getNavarea() {
  try {
    const response = await elBackAxios.get(`/api/navarea/`)
    return response.data
  } catch (error) {
    console.error(`Failed to get navarea data:`, error)
    throw error
  }
}

/**
 * Проверить подписку на сервис Navarea для корабля
 * Использует кэшированные данные из momentaryStore
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<boolean>} - true если подписка активна, false иначе
 */
export async function hasNavareaSubscription(deviceId) {
  try {
    const { useMomentaryStore } = await import('@/stores/momentary')
    const momentaryStore = useMomentaryStore()
    
    // Проверяем кэшированные подписки
    return momentaryStore.hasServiceSubscription('Navarea')
  } catch (error) {
    console.error(`Failed to check Navarea subscription for device ${deviceId}:`, error)
    return false
  }
}

/**
 * Проверить подписку на сервис AIS-Around для корабля
 * Использует кэшированные данные из momentaryStore
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<boolean>} - true если подписка активна, false иначе
 */
export async function hasAISAroundSubscription(deviceId) {
  try {
    const { useMomentaryStore } = await import('@/stores/momentary')
    const momentaryStore = useMomentaryStore()
    
    // Проверяем кэшированные подписки на AIS-Around
    return momentaryStore.hasServiceSubscription('AIS-Around')
  } catch (error) {
    console.error(`Failed to check AIS-Around subscription for device ${deviceId}:`, error)
    return false
  }
}

/**
 * Проверить подписку на сервис AIS-Ship для корабля
 * Использует кэшированные данные из momentaryStore
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<boolean>} - true если подписка активна, false иначе
 */
export async function hasAISShipSubscription(deviceId) {
  try {
    const { useMomentaryStore } = await import('@/stores/momentary')
    const momentaryStore = useMomentaryStore()
    
    // Проверяем кэшированные подписки на AIS-Ship
    return momentaryStore.hasServiceSubscription('AIS-Ship')
  } catch (error) {
    console.error(`Failed to check AIS-Ship subscription for device ${deviceId}:`, error)
    return false
  }
}

/**
 * Проверить подписку на сервис Weather для корабля
 * Использует кэшированные данные из momentaryStore
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<boolean>} - true если подписка активна, false иначе
 */
export async function hasWeatherSubscription(deviceId) {
  try {
    const { useMomentaryStore } = await import('@/stores/momentary')
    const momentaryStore = useMomentaryStore()
    
    // Проверяем кэшированные подписки
    return momentaryStore.hasServiceSubscription('Weather')
  } catch (error) {
    console.error(`Failed to check Weather subscription for device ${deviceId}:`, error)
    return false
  }
}

/**
 * Проверить подписку на сервис Passage-Plan для корабля
 * Использует кэшированные данные из momentaryStore
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<boolean>} - true если подписка активна, false иначе
 */
export async function hasPassagePlanSubscription(deviceId) {
  try {
    const { useMomentaryStore } = await import('@/stores/momentary')
    const momentaryStore = useMomentaryStore()
    
    // Проверяем кэшированные подписки на Passage-Plan
    return momentaryStore.hasServiceSubscription('Passage-Plan')
  } catch (error) {
    console.error(`Failed to check Passage-Plan subscription for device ${deviceId}:`, error)
    return false
  }
}

/**
 * Проверить подписку на сервис Messengers для корабля
 * Использует кэшированные данные из momentaryStore
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<boolean>} - true если подписка активна, false иначе
 */
export async function hasMessengersSubscription(deviceId) {
  try {
    const { useMomentaryStore } = await import('@/stores/momentary')
    const momentaryStore = useMomentaryStore()
    
    // Проверяем кэшированные подписки на Messengers
    return momentaryStore.hasServiceSubscription('Messengers')
  } catch (error) {
    console.error(`Failed to check Messengers subscription for device ${deviceId}:`, error)
    return false
  }
}

// Константа для recipient_user_id (позже поменяем)
// const SERVICE_REQUEST_RECIPIENT_USER_ID = '111040'
// const SERVICE_REQUEST_RECIPIENT_USER_ID = '93384'
const SERVICE_REQUEST_RECIPIENT_USER_ID = '74350'

/**
 * Запросить подписку на сервис
 * Логирует в консоль и отправляет email-запрос на backend
 * 
 * @param {string} serviceKey - Ключ сервиса (AIS-Ship, Weather и т.д.)
 * @param {string} serviceName - Красивое имя сервиса (AIS Ship, Weather и т.д.)
 * @param {Object} shipInfo - Информация о корабле из momentaryStore
 *   {deviceId, shipData, userData, agreementData}
 * @returns {Promise<void>}
 */
export async function requestService(serviceKey, serviceName, shipInfo) {
  try {
    const {
      deviceId,
      shipData = {},
      userData = {},
      agreementData = {}
    } = shipInfo || {}

    // Логируем запрос в консоль
    console.log('[RequestService] === Service Request Details ===')
    console.log('[RequestService] DeviceId:', deviceId || 'N/A')
    console.log('[RequestService] Ship Name:', shipData?.name || 'N/A')
    console.log('[RequestService] Agreement ID:', agreementData?.displayId || 'N/A')
    console.log('[RequestService] Agreement Name:', agreementData?.name || 'N/A')
    console.log('[RequestService] Account Email:', userData?.email || 'N/A')
    console.log('[RequestService] Service Key:', serviceKey)
    console.log('[RequestService] Service Name:', serviceName)
    console.log('[RequestService] Request Timestamp:', new Date().toISOString())
    console.log('[RequestService] ==============================')

    // Формируем тему и тело письма
    const subject = `Service Request: ${serviceName} for ${shipData?.name || 'Unknown Ship'}`
    
    const body = `
Service Request Details
=======================

Service: ${serviceName}
Timestamp: ${new Date().toISOString()}

Ship Information:
- Name: ${shipData?.name || 'N/A'}
- DeviceId: ${deviceId || 'N/A'}
- Account Email: ${userData?.email || 'N/A'}

Agreement Information:
- Agreement ID: ${agreementData?.displayId || 'N/A'}
- Agreement Name: ${agreementData?.name || 'N/A'}

Please process this service request for the specified ship.
    `.trim()

    // Отправляем email-запрос на backend
    await sendServiceRequestEmail(
      SERVICE_REQUEST_RECIPIENT_USER_ID,
      subject,
      body
    )

    console.log('[RequestService] Email request sent successfully')

  } catch (error) {
    console.error('[RequestService] Error:', error)
  }
}

/**
 * Отправить email-запрос по подписке на сервис
 * 
 * @param {string} recipientUserId - ID пользователя для отправки
 * @param {string} subject - Тема письма
 * @param {string} body - Тело письма
 * @returns {Promise<Object>}
 */
export async function sendServiceRequestEmail(recipientUserId, subject, body) {
  try {
    const response = await elBackAxios.post('api/email/send', {
      recipient_user_id: recipientUserId,
      subject: subject,
      body: body
    })
    
    console.log('[SendEmail] Email sent successfully:', response.data)
    return response.data
  } catch (error) {
    console.error('[SendEmail] Failed to send email:', error)
    throw error
  }
}

/**
 * Скачать сертификат продукта для корабля
 * Использует myAxios для обращения к mwend
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<Blob>}
 */
export async function downloadProductCertificate(deviceId) {
  try {
    const response = await myAxios.get(`${getMwendBaseUrl()}/api/v1/product_certificate?deviceId=${deviceId}`, {
      responseType: 'blob'
    })
    return response.data
  } catch (error) {
    console.error(`Failed to download product certificate for device ${deviceId}:`, error)
    throw error
  }
}

/**
 * Скачать вложение сертификата продукта для корабля
 * Использует myAxios для обращения к mwend
 * @param {string} deviceId - ID устройства (корабля)
 * @returns {Promise<Blob>}
 */
export async function downloadProductCertificateAttachment(deviceId) {
  try {
    const response = await myAxios.get(`${getMwendBaseUrl()}/api/v1/product_certificate_attachment?deviceId=${deviceId}`, {
      responseType: 'blob'
    })
    return response.data
  } catch (error) {
    console.error(`Failed to download product certificate attachment for device ${deviceId}:`, error)
    throw error
  }
}

export default {
  getAllDevicesWithServices,
  getDeviceServices,
  subscribeToService,
  pauseService,
  resumeService,
  unsubscribeFromService,
  getStatusInfo,
  isDateExpired,
  formatDate,
  getAllServicesHealth,
  getServiceHealth,
  getNavarea,
  hasNavareaSubscription,
  hasAISAroundSubscription,
  hasAISShipSubscription,
  hasWeatherSubscription,
  hasPassagePlanSubscription,
  hasMessengersSubscription,
  requestService,
  sendServiceRequestEmail,
  parseDeviceSubscriptions,
  downloadProductCertificate,
  downloadProductCertificateAttachment
}

/**
 * Парсить ответ API подписок и преобразовать в формат для хранения в store
 * Преобразует массив подписок в объект вида: { 'ServiceType': true/false, ... }
 * 
 * @param {Array} servicesArray - Массив подписок из API (response.data)
 * @returns {Object} - Объект подписок: { 'Navarea': true, 'AIS-Around': false, ... }
 */
export function parseDeviceSubscriptions(servicesArray) {
  const subscriptions = {}

  if (!Array.isArray(servicesArray)) {
    return subscriptions
  }

  // Проходим по всем сервисам и добавляем в объект
  servicesArray.forEach(service => {
    const serviceType = service.ServiceType || 'Unknown'
    const isActive = service.Status === 'Active'
    subscriptions[serviceType] = isActive
  })

  return subscriptions
}
